import { generateGid } from '@theme/global-utilities';

/** @typedef {string} ShopifyProductGid */

/** Minimal swatch entry. */
/** @typedef {{ productId: ShopifyProductGid, isChecked?: boolean }} ProductCardSwatch */

/** Minimal product card shape we output to slideshow-renderer. */
/** @typedef {{ id: ShopifyProductGid, swatches: ProductCardSwatch[] }} MinimalProductCard */



/**
 * Collect all products from all wishlists and
 * shape them into MinimalProductCard objects.
 *
 * @returns {MinimalProductCard[]}
 */
function collectWishlistProducts() {
  /** @type {Map<ShopifyProductGid, ProductCardSwatch[]>} */
  const productToSwatches = new Map();

  window.swymWishLists?.forEach((list) => {
    list.listcontents?.forEach((item) => {
      if (!item?.empi) return;

      const productGid = generateGid(item.empi);

      if (!productToSwatches.has(productGid)) {
        const rawSwatches = item.cprops?.swatches ?? [];

        /** @type {ProductCardSwatch[]} */
        const swatches = rawSwatches.map((s) => ({
          productId: s.productId,
        }));

        productToSwatches.set(productGid, swatches);
      }
    });
  });

  return Array.from(productToSwatches.entries()).map(
    ([productId, swatches]) => ({
      id: productId,
      swatches,
    })
  );
}

class WishlistCarousel extends HTMLElement {
  constructor() {
    super();
    /** @private @type {number} */
    this.size = 8;
    /** @private @type {boolean} */
    this._mounted = false;
  }

  connectedCallback() {
    if (this._mounted) return;
    this._mounted = true;

    const attrSize = Number(
      this.getAttribute('data-size') || this.dataset.size
    );
    this.size = Number.isFinite(attrSize) && attrSize > 0 ? attrSize : 8;

    window.swymcsWishlistFunctions.onWishlistsReady(() => {
      this._render();
    });
  }

  /**
   * @private
   * @returns {void}
   */
  _render() {
    try {
      /** @type {MinimalProductCard[]} */
      const items = collectWishlistProducts();

      const json = JSON.stringify(items);
      const renderer = document.createElement('slideshow-renderer');
      renderer.setAttribute('data-json', json);

      this.replaceChildren(renderer);
    } catch {
      this.textContent = '';
    }
  }
}

if (!customElements.get('wishlist-carousel')) {
  customElements.define('wishlist-carousel', WishlistCarousel);
}
